import { CUSTOMER_BONUS_PACKS } from '../data/bonusPackCatalog';
import type { BonusPack } from '../types/domain';

interface MockBonusListOptions {
  includeUnpublished?: boolean;
  includeDeleted?: boolean;
  claimedBonusIds?: string[];
}

let mockBonuses: BonusPack[] = CUSTOMER_BONUS_PACKS.map((pack) => ({
  ...pack,
  published: pack.published ?? true,
}));
let mockClaimedBonuses: string[] = [];

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function listMockBonuses(options?: MockBonusListOptions): BonusPack[] {
  const claimedBonusIds = new Set(options?.claimedBonusIds ?? []);
  return clone(
    mockBonuses
      .filter(
        (pack) =>
          options?.includeUnpublished || pack.published !== false || claimedBonusIds.has(pack.id)
      )
      .filter((pack) =>
        options?.includeDeleted ? true : !pack.deletedAt || claimedBonusIds.has(pack.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name))
  );
}

export function getMockBonus(
  id: string,
  options?: { includeUnpublished?: boolean; includeDeleted?: boolean; claimedBonusIds?: string[] }
): BonusPack | null {
  const pack = mockBonuses.find((candidate) => candidate.id === id);
  if (!pack) return null;
  const claimedBonusIds = new Set(options?.claimedBonusIds ?? []);
  if (pack.published === false && !options?.includeUnpublished && !claimedBonusIds.has(pack.id))
    return null;
  if (pack.deletedAt && !options?.includeDeleted && !claimedBonusIds.has(pack.id)) return null;
  return clone(pack);
}

export function listMockClaimedBonuses(): string[] {
  return [...mockClaimedBonuses];
}

export function claimMockBonus(id: string): string[] {
  if (!mockClaimedBonuses.includes(id)) {
    mockClaimedBonuses = [...mockClaimedBonuses, id];
  }
  return [...mockClaimedBonuses];
}

export function createMockBonus(pack: BonusPack): BonusPack {
  const created = clone({ ...pack, published: pack.published ?? true });
  mockBonuses = [created, ...mockBonuses].sort((a, b) => a.name.localeCompare(b.name));
  return clone(created);
}

export function updateMockBonus(id: string, patch: Partial<BonusPack>): BonusPack | null {
  const current = mockBonuses.find((candidate) => candidate.id === id);
  if (!current) return null;
  const updated = {
    ...current,
    ...clone(patch),
    id: current.id,
    published: patch.published ?? current.published ?? true,
  };
  mockBonuses = mockBonuses
    .map((candidate) => (candidate.id === id ? updated : candidate))
    .sort((a, b) => a.name.localeCompare(b.name));
  return clone(updated);
}

export function deleteMockBonus(id: string): void {
  mockBonuses = mockBonuses
    .map((candidate) =>
      candidate.id === id
        ? { ...candidate, deletedAt: candidate.deletedAt ?? new Date().toISOString() }
        : candidate
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function mockBonusesReset(): void {
  mockBonuses = CUSTOMER_BONUS_PACKS.map((pack) => ({
    ...pack,
    published: pack.published ?? true,
  }));
  mockClaimedBonuses = [];
}
